import React, { useEffect, useState } from "react";
import axios from "axios";
import { Prose } from "@/components/site/Prose";
import { useI18n } from "@/i18n/I18nContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_FALLBACK = "https://images.unsplash.com/photo-1633978077821-6b1b16a176a4?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85";
const DEFAULT_EXTRAS = [
  "https://images.unsplash.com/photo-1629670670137-c0a865ad29ce?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85",
  "https://images.unsplash.com/photo-1777552955794-77aa990a47df?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85",
];

const DIVE_NOTES_BY_LANG = {
  en: ["Pelagics, oceanic whitetips", "Spinner dolphins year-round", "House reef, family friendly", "Coral pinnacles, macro life"],
  de: ["Großfische, Weißspitzen-Hochseehaie", "Das ganze Jahr Spinner-Delfine", "Hausriff, familienfreundlich", "Korallenpinnakel, Makroleben"],
  fr: ["Pélagiques, requins longimanes", "Dauphins à long bec toute l'année", "Récif maison, familial", "Pinacles coralliens, vie macro"],
  it: ["Pelagici, squali longimano", "Delfini stenella tutto l'anno", "Reef di casa, per famiglie", "Pinnacoli di corallo, vita macro"],
  ru: ["Пелагические, длиннокрылые акулы", "Дельфины-стенеллы круглый год", "Домашний риф, для семей", "Коралловые пики, макрожизнь"],
  ar: ["أسماك البحر المفتوح وقرش المحيط", "دلافين الدوار طوال العام", "شعاب الفندق، مناسبة للعائلات", "قمم مرجانية وحياة مجهرية"],
  zh: ["远洋鱼类、远洋白鳍鲨", "全年可见长吻原海豚", "家园礁、适合家庭", "珊瑚尖塔、微距生物"],
  ja: ["外洋性の魚、ヨゴレ", "年間を通してハシナガイルカ", "ハウスリーフ、家族向け", "珊瑚のピナクル、マクロ生物"],
  da: ["Pelagiske, oceaniske hvidtippede hajer", "Spinner-delfiner hele året", "Husrev, familievenligt", "Koralspir, makroliv"],
};

export default function BeachesPage() {
  const { t, lang, content } = useI18n();
  const hero = content?.beaches?.image_url || HERO_FALLBACK;
  const notes = DIVE_NOTES_BY_LANG[lang] || DIVE_NOTES_BY_LANG.en;
  const [extras, setExtras] = useState(DEFAULT_EXTRAS);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/images/beaches_extras`).then((r) => {
      if (cancelled) return;
      const arr = r?.data?.images;
      if (Array.isArray(arr) && arr.length === 2) setExtras(arr);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const [SAND, REEF] = extras;
  const sites = [
    { name: "Elphinstone Reef", depth: "20–40m", note: notes[0] },
    { name: "Sataya — Dolphin Reef", depth: "5–15m", note: notes[1] },
    { name: "El Kaff", depth: "8–25m", note: notes[2] },
    { name: "El Erg", depth: "12–30m", note: notes[3] },
  ];
  return (
    <main className="min-h-screen" data-testid="beaches-page">
      <section className="relative h-[55vh] min-h-[420px] overflow-hidden">
        <img src={hero} alt="Red Sea reef" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-[var(--bg-base)]" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 h-full flex items-end pb-14">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-white/85 font-bold">Chapter III</p>
            <h1 className="font-display text-white text-5xl sm:text-7xl lg:text-8xl tracking-tight">{t("section_beaches")} & {t("section_diving")}</h1>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <Prose section="beaches" fallbackTitle="Quiet beaches, untouched coastline" />
        </div>
        <div className="lg:col-span-5">
          <img src={SAND} alt="Sand" className="rounded-3xl w-full aspect-[4/5] object-cover" />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 lg:order-2">
          <Prose section="diving" fallbackTitle="One of the world's great dive coasts" />
        </div>
        <div className="lg:col-span-7 lg:order-1">
          <img src={REEF} alt="Coral" className="rounded-3xl w-full aspect-[4/3] object-cover" />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-24" data-testid="dive-spots">
        <h2 className="font-display text-3xl text-stone-900 mb-8">{t("dive_sites_title")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {sites.map((s, i) => (
            <div key={i} className="rounded-2xl border border-stone-200 bg-[var(--bg-surface)] p-6" data-testid={`dive-${i}`}>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--brand-primary)]">{s.depth}</p>
              <h3 className="font-display text-xl mt-1.5">{s.name}</h3>
              <p className="text-sm text-stone-600 mt-2">{s.note}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
